import React from 'react';
import autoBind from 'react-autobind';
import { If, Then } from 'react-if';
import Localization from "../Localization.jsx";
import Config from "../Config.jsx";
import ProfileDialog from "../views/ProfileDialog.jsx";
import ChangePasswordDialog from "../views/ChangePasswordDialog.jsx";
import AdminTaskDialog from "../views/AdminTaskDialog.jsx";
import SubscriptionDialog from "../views/SubscriptionDialog.jsx";
import StorageInfoDialog from "../views/StorageInfoDialog.jsx";
import EDConnectDialog from "../views/EDConnectDialog.jsx";
import SettingsDialog from "../views/SettingsDialog.jsx";
import ExtraLocaleDialog from "../views/ExtraLocaleDialog.jsx";
import SchoolActivityDialog from "../views/SchoolActivityDialog.jsx";
import { Box, Group, Text, Menu, Tooltip, UnstyledButton } from '@mantine/core';
import {
  IconUserCircle,
  IconEdit,
  IconLock,
  IconSettings,
  IconReceipt,
  IconDatabase,
  IconLogout,
  IconUserShield,
  IconLink,
  IconBooks,
  IconHistory,
} from '@tabler/icons-react';
import { BRAND_PRIMARY } from "../theme/brandTheme.js";
import AuthService from "../tools/AuthService.js";
import { showEdConnectMenuItem } from "../tools/accountMenu.js";

/**
 * How long to wait for the token revocation before handing the browser to
 * EdCity's logout anyway.
 *
 * Generous enough that the revocation normally completes — it is one small POST
 * — but bounded, because the EdCity hop waits behind it and ending the CAS
 * session is the leg that protects the NEXT person at this machine.
 */
const LOGOUT_REVOKE_TIMEOUT_MS = 4000;

const styles = {
  accountButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 14px',
    borderRadius: '999px',
    color: 'white',
    cursor: 'pointer',
    backgroundColor: 'rgba(255,255,255,0.08)',
    maxWidth: '320px',
  },
  accountText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  accountPrimary: {
    fontSize: '0.95rem',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  accountSecondary: {
    fontSize: '0.78rem',
    lineHeight: 1.2,
    opacity: 0.85,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}

class TopBar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      menuOpened: false,
      isProfileDialogOpen: false,
      isSubscriptionDialogOpen: false,
      isEDConnectDialogOpen: false,
      isSettingsDialogOpen: false,
      isExtraLocaleDialogOpen: false,
      isSchoolActivityDialogOpen: false,
    };
    autoBind(this);
  }

  // School Activity (登入記錄 + LOCALE 操作記錄) — school admins, role >= 2.
  // The menu item and the dialog mount share the same predicate below, so a
  // visible item can never open a dialog that was not rendered.
  handleSchoolActivityDialogOpen = () => {
    this.handleMenuClose();
    this.setState({ isSchoolActivityDialogOpen: true });
  };

  handleSchoolActivityDialogClose = () => {
    this.setState({ isSchoolActivityDialogOpen: false });
  };

  componentDidMount() {
    const userdata = localStorage.getItem('userdata');
    if (userdata) {
      try {
        const parsed_userdata = JSON.parse(userdata);

        // Handle both new format (with loginResponse) and old format (direct response)
        const loginResponse = parsed_userdata.loginResponse || parsed_userdata;
        const userinfo = JSON.parse(loginResponse.text);

        const dn = userinfo?.user_info?.display_name;
        if (typeof dn === 'string' && dn.startsWith('s-')) {
          this.props.root.addNotification("Please change default display name", false);
          this.profileClicked();
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }

    // Fetch subscription detail once on mount, when refs are ready.
    // (Previously called on every render, which fired redundant API requests.)
    if (this.props.root.refs && this.props.root.refs.API && this.props.root.refs.userDataProfile) {
      this.props.root.getSubscriptionDetailBySchoolId();
    }
  }

  handleSubscriptionDialogOpen = () => {
    this.props.root.getSubscriptionDetailBySchoolId();
    this.handleMenuClose();
    this.setState({ isSubscriptionDialogOpen: true });
  };

  handleSubscriptionDialogClose = () => {
    this.setState({ isSubscriptionDialogOpen: false });
  };

  handleStorageInfoDialogOpen = () => {
    this.handleMenuClose();
    if (this.refs.storageInfoDialog) {
      this.refs.storageInfoDialog.show();
    }
  };

  handleMenuClose = () => {
    this.setState({ menuOpened: false });
  };

  profileClicked = () => {
    this.setState({ isProfileDialogOpen: true });
    this.handleMenuClose();
  };

  handleProfileDialogClose = () => {
    this.setState({ isProfileDialogOpen: false });
  };

  handleEDConnectDialogOpen = () => {
    this.setState({ isEDConnectDialogOpen: true });
    this.handleMenuClose();
  };

  handleEDConnectDialogClose = () => {
    this.setState({ isEDConnectDialogOpen: false });
  };

  handleExtraLocaleDialogOpen = () => {
    this.setState({ isExtraLocaleDialogOpen: true });
    this.handleMenuClose();
  };

  handleExtraLocaleDialogClose = () => {
    this.setState({ isExtraLocaleDialogOpen: false });
  };

  changePasswordClicked() {
    this.refs.changePasswordDialog.setState({open: true});
    this.handleMenuClose();
  }

  /**
   * Sign out — of the composer, of the API, and of EdCity when the session came
   * from there.
   *
   * The EdCity leg is the part that is easy to leave out and that users notice:
   * clearing our own session does nothing to EdCity's SSO cookie, which is
   * HttpOnly and on their origin, and their authorize endpoint looks for an
   * active session before it shows a login form. So "log out, then log back in
   * with EdCity" used to skip the password prompt entirely and hand the account
   * back to whoever had just left — a real problem on a shared staffroom PC.
   *
   * The screen is switched FIRST and the network work runs after, so signing out
   * still feels instant; the redirect to EdCity is the last thing that happens,
   * once the token revocation has actually been sent.
   *
   * NOTHING here clears the `ev_edcity_sso` flag. Reading it used to clear it,
   * as the first statement of this method, and then several awaits stood between
   * that and the navigation — so a stalled revocation, or a teacher shutting the
   * tab as soon as the login card appeared, left the CAS cookie alive with the
   * flag already gone. The next EdConnect press went straight to authorize,
   * found that session, and signed the previous teacher back in. The flag is now
   * dropped only when the browser actually returns from EdCity's logout
   * (RootPage, `?edcity_logout=1`), so every way of not getting there leaves it
   * set and costs one extra logout hop next time — the harmless direction.
   */
  async logoutClicked() {
    this.handleMenuClose();
    // Built BEFORE the teardown, because it reads a flag the teardown may touch.
    // It does not consume it — see the note above.
    const edCityLogoutUrl = AuthService.edCityLogoutUrl();

    this.props.root.refs.API.request("do_logout");
    this.props.root.changeStatus(this.props.root.STATUS_REQUIRE_LOGIN());
    localStorage.removeItem('userdata');
    this.props.root.replaceHistory("/");

    // The legacy `do_logout` cmd is a bare ack that writes nothing, so without
    // this the 30-day refresh token stayed valid on the server after logout.
    //
    // Bounded, because the EdCity hop is queued behind it and `authFetch` has no
    // timeout of its own: a stalled revocation would otherwise mean the CAS
    // session is never ended at all. Losing the revocation is the lesser harm —
    // the refresh token expires on its own, whereas a live CAS session hands the
    // next person this account. The flag survives either way, so a logout that
    // times out here is still repaired by the next sign-in.
    await AuthService.logoutWithin(LOGOUT_REVOKE_TIMEOUT_MS);

    if (edCityLogoutUrl) {
      window.location.href = edCityLogoutUrl;
    }
  }

  adminTaskClicked() {
    this.handleMenuClose();
    this.refs.adminTaskDialog.openDialog();
  }

  handleSettingsDialogOpen = () => {
    this.handleMenuClose();
    this.setState({ isSettingsDialogOpen: true });
  };

  handleSettingsDialogClose = () => {
    this.setState({ isSettingsDialogOpen: false });
  };

  render() {
    // The dialog now hosts BOTH the legacy EdConnect binding and the new
    // Google/Apple links — reachable when either half is enabled, or the
    // linked-accounts UI would exist with no way to open it.
    const showEDConnectBinding = typeof Config.ENABLE_EDCONNECT_BINDING === 'function'
      ? Config.ENABLE_EDCONNECT_BINDING()
      : false;
    // Menu item and dialog mount stay on the same predicate so tapping a
    // visible item cannot open a dialog that was never rendered.
    // Safe access to user role
    const userRole = this.props.root.refs && this.props.root.refs.userDataProfile && this.props.root.refs.userDataProfile.state.userInfo
      ? this.props.root.refs.userDataProfile.state.userInfo.role
      : null;
    const userDataProfile = this.props.root.refs && this.props.root.refs.userDataProfile
      ? this.props.root.refs.userDataProfile.state
      : null;
    const userInfo = userDataProfile ? userDataProfile.userInfo || {} : {};
    const schoolInfo = userDataProfile ? userDataProfile.schoolInfo || {} : {};
    const displayName = userInfo.display_name || userInfo.username || Localization.strings.ACCOUNT;
    const schoolName = Localization.strings.getLanguage() === 'en'
      ? (schoolInfo.name_eng || schoolInfo.name || '')
      : (schoolInfo.name || schoolInfo.name_eng || '');
    // The school's enrolment key, shown beside the school name so a teacher can
    // read the key they hand to students without opening Admin Task (which is
    // role >= 2 anyway, so most teachers cannot).
    //
    // IN THE DROPDOWN ONLY, never on the always-visible top bar. The bar is on
    // screen whenever the composer is — including while a teacher projects it to
    // a class — and this key is what /api/auth/device accepts as proof of a
    // school. Behind a deliberate click it is a convenience; painted permanently
    // across the top of a projector it is a broadcast.
    //
    // Empty for a school that has no key and for an unbound account, and absent
    // entirely from an older API — every case renders no chip rather than a
    // blank one.
    const schoolKey = String(schoolInfo.key || '').trim();
    const showAccountLinking = showEdConnectMenuItem({
      bindingEnabled: showEDConnectBinding,
      googleBindingEnabled: typeof Config.ENABLE_GOOGLE_BINDING === 'function'
        && Config.ENABLE_GOOGLE_BINDING(),
      appleLoginEnabled: typeof Config.ENABLE_APPLE_LOGIN === 'function'
        && Config.ENABLE_APPLE_LOGIN(),
      hasPassword: userInfo.has_password,
    });
    // Extra Locale (預製教材範本) selection — offered only when the school's
    // ACTIVE subscription carries a PLAN_EXTRA_LOCALE allowance. The privilege
    // value is a string here (UserDataProfile normalises), and the server
    // re-checks eligibility on every call, so this test only decides menu
    // visibility. `subscriptionPlan === 'Premium'` = "has an active
    // subscription" (get_privilege_detail's wording), guarding against a
    // future non-zero catalog default leaking the item to lapsed schools.
    const extraLocalePrivilege = this.props.root.getPrivilege
      ? this.props.root.getPrivilege('PLAN_EXTRA_LOCALE')
      : null;
    const showExtraLocale = !!(
      userDataProfile
      && userDataProfile.subscriptionPlan === 'Premium'
      && extraLocalePrivilege
      && Number(extraLocalePrivilege.value) > 0
    );

    return (
      <div>
        <Box
          component="header"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            backgroundColor: BRAND_PRIMARY,
            color: 'white',
          }}
        >
          <Group h={64} px="md" justify="space-between" wrap="nowrap">
            <Text c="white" fw={500} fz="1.5rem" truncate>
              <If condition={this.props.root.state.status !== this.props.root.STATUS_VIEW_HOTSPOT()}>
                <Then>
                  {this.props.root.state.status_titles[this.props.root.state.status]}
                </Then>
              </If>

              <If condition={this.props.root.state.status === this.props.root.STATUS_VIEW_HOTSPOT()}>
                <Then>
                  <If condition={this.props.root.refs.hotspotViewerPage && this.props.root.refs.hotspotViewerPage.state.currentView === 0}>
                    <Then>
                      {this.props.root.state.currentLocale && this.props.root.state.currentLocale.name
                        ? `${this.props.root.state.currentLocale.name} ${Localization.strings.HOTSPOT_LIST}`
                        : Localization.strings.HOTSPOT_LIST}
                    </Then>
                  </If>

                  <If condition={this.props.root.refs.hotspotViewerPage && this.props.root.refs.hotspotViewerPage.state.currentView === 1}>
                    <Then>
                      {Localization.strings.HOTSPOT_EDIT}
                    </Then>
                  </If>
                </Then>
              </If>
            </Text>

            {/* Account button + menu */}
            <Menu
              opened={this.state.menuOpened}
              onChange={(opened) => this.setState({ menuOpened: opened })}
              position="bottom-end"
              offset={10}
              width={280}
              radius="md"
              shadow="md"
              zIndex={1300}
              withinPortal
            >
              <Menu.Target>
                <UnstyledButton
                  style={styles.accountButton}
                  aria-label={Localization.strings.ACCOUNT_SETTINGS}
                >
                  <IconUserCircle size={34} color="white" stroke={1.5} />
                  <div style={styles.accountText}>
                    <Text c="white" style={styles.accountPrimary}>{displayName}</Text>
                    <Text c="white" style={styles.accountSecondary}>{schoolName}</Text>
                  </div>
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown>
                <div style={{ padding: '8px 16px' }}>
                  <Text fw={600} style={{ lineHeight: 1.2 }}>{displayName}</Text>
                  <Group gap={6} mt={4} wrap="wrap" align="center">
                    <Text size="sm" c="dimmed">{schoolName}</Text>
                    {schoolKey !== '' && (
                      <Tooltip label={Localization.strings.SCHOOL_KEY_HINT} withArrow>
                        {/* Labelled rather than a bare chip: on its own the key
                            is four anonymous characters, and 「學校密鑰」 is the
                            phrase the app's own key-entry dialog uses, so a
                            teacher reading it here recognises what to hand over.
                            The value stays monospace — these keys mix digits and
                            letters and get read aloud or copied by hand. */}
                        <Text size="sm" c="dimmed" style={{ cursor: 'help' }}>
                          {Localization.strings.SCHOOL_KEY_LABEL_OPEN}
                          <span style={{ fontFamily: 'monospace' }}>{schoolKey}</span>
                          {Localization.strings.SCHOOL_KEY_LABEL_CLOSE}
                        </Text>
                      </Tooltip>
                    )}
                  </Group>
                </div>
                <Menu.Divider />
                <Menu.Item leftSection={<IconEdit size={18} />} onClick={this.profileClicked}>
                  {Localization.strings.EDIT_PROFILE}
                </Menu.Item>
                <Menu.Item leftSection={<IconLock size={18} />} onClick={this.changePasswordClicked}>
                  {Localization.strings.CHANGE_PASSWORD}
                </Menu.Item>
                <Menu.Item leftSection={<IconSettings size={18} />} onClick={this.handleSettingsDialogOpen}>
                  {Localization.strings.APP_SETTINGS}
                </Menu.Item>
                <Menu.Item leftSection={<IconReceipt size={18} />} onClick={this.handleSubscriptionDialogOpen}>
                  {Localization.strings.SUBSCRIPTION}
                </Menu.Item>
                <Menu.Item leftSection={<IconDatabase size={18} />} onClick={this.handleStorageInfoDialogOpen}>
                  {Localization.strings.STORAGE}
                </Menu.Item>
                {showAccountLinking && (
                  <Menu.Item leftSection={<IconLink size={18} />} onClick={this.handleEDConnectDialogOpen}>
                    {showEDConnectBinding
                      ? Localization.strings.EDCONNECT_BINDING
                      : Localization.strings.LINKED_SIGNIN_METHODS}
                  </Menu.Item>
                )}
                {showExtraLocale && (
                  <Menu.Item leftSection={<IconBooks size={18} />} onClick={this.handleExtraLocaleDialogOpen}>
                    {Localization.strings.EXTRA_LOCALE_MENU}
                  </Menu.Item>
                )}
                {Number(userRole) >= 2 && (
                  <Menu.Item leftSection={<IconHistory size={18} />} onClick={this.handleSchoolActivityDialogOpen}>
                    {Localization.strings.SCHOOL_ACTIVITY_MENU || 'School Activity'}
                  </Menu.Item>
                )}
                {Number(userRole) >= 2 && (
                  <Menu.Item leftSection={<IconUserShield size={18} />} onClick={this.adminTaskClicked}>
                    {Localization.strings.ADMIN_TASK}
                  </Menu.Item>
                )}
                <Menu.Divider />
                <Menu.Item color="red" leftSection={<IconLogout size={18} />} onClick={this.logoutClicked}>
                  {Localization.strings.LOGOUT}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Box>

        {/* Dialog Components */}
        <ProfileDialog parent={this} open={this.state.isProfileDialogOpen} onClose={this.handleProfileDialogClose} root={this.props.root} ref="profileDialog" style={{ zIndex: 1500 }} />
        <ChangePasswordDialog parent={this} root={this.props.root} ref="changePasswordDialog" style={{ zIndex: 1500 }} />
        <AdminTaskDialog parent={this} root={this.props.root} ref="adminTaskDialog" style={{ zIndex: 1500 }} />
        <SubscriptionDialog open={this.state.isSubscriptionDialogOpen} onClose={this.handleSubscriptionDialogClose} root={this.props.root} />
        <StorageInfoDialog root={this.props.root} parent={this} ref="storageInfoDialog" />
        <SettingsDialog open={this.state.isSettingsDialogOpen} onClose={this.handleSettingsDialogClose} root={this.props.root} />
        {showAccountLinking && (
          <EDConnectDialog open={this.state.isEDConnectDialogOpen} onClose={this.handleEDConnectDialogClose} root={this.props.root} hasPassword={userInfo.has_password} />
        )}
        <ExtraLocaleDialog open={this.state.isExtraLocaleDialogOpen} onClose={this.handleExtraLocaleDialogClose} root={this.props.root} />
        {Number(userRole) >= 2 && (
          <SchoolActivityDialog open={this.state.isSchoolActivityDialogOpen} onClose={this.handleSchoolActivityDialogClose} root={this.props.root} />
        )}
      </div>
    );
  }
}

export default TopBar;
